/**
 * Charset para tickets ESC/POS: el texto se emite en CP850 (codepage 2),
 * el estándar latin1-multinational que soportan las térmicas genéricas.
 *
 * Reglas:
 * - Chars ASCII imprimibles pasan directo.
 * - Letras acentuadas/ñ/¿/¡ se mapean a su byte CP850.
 * - Puntuación tipográfica se sustituye por el ASCII equivalente.
 * - Cualquier char de control (<0x20, salvo \n \t) o no mapeable jamás se emite
 *   crudo: NFD + strip de diacríticos, y si sigue sin ser ASCII → '?'.
 *   Esto evita que un nombre de producto con comillas/emoji inyecte comandos
 *   ESC/POS (ej. '"' + 'V' = corte prematuro).
 */

const CP850: Record<string, number> = {
  á: 0xa0, é: 0x82, í: 0xa1, ó: 0xa2, ú: 0xa3, ñ: 0xa4, Ñ: 0xa5,
  Á: 0xb5, É: 0x90, Í: 0xd6, Ó: 0xe0, Ú: 0xe9,
  ü: 0x81, Ü: 0x9a, ç: 0x87, Ç: 0x80,
  '¿': 0xa8, '¡': 0xad, 'º': 0xa7, 'ª': 0xa6,
  '×': 0x9e, '·': 0xfa, '÷': 0xf6, '±': 0xf1,
  // Puntuación tipográfica → ASCII
  '–': 0x2d, '—': 0x2d, '−': 0x2d, '‐': 0x2d, '‑': 0x2d,
  '‘': 0x27, '’': 0x27, '‚': 0x27, '′': 0x27,
  '“': 0x22, '”': 0x22, '„': 0x22, '″': 0x22,
  '…': 0x2e, '•': 0x2e, '°': 0xf8, '€': 0x24,
};

/** Texto → bytes CP850 seguros para ESC/POS. */
export function encodeEscPosChars(text: string): number[] {
  const out: number[] = [];
  for (const ch of text.normalize('NFC')) {
    const cp = ch.codePointAt(0)!;
    if (cp === 0x0a || cp === 0x09) {
      out.push(cp);
    } else if (cp < 0x20 || cp === 0x7f) {
      continue; // nunca emitir bytes de control desde datos
    } else if (cp <= 0x7e) {
      out.push(cp);
    } else {
      const mapped = CP850[ch];
      if (mapped != null) {
        out.push(mapped);
        continue;
      }
      const stripped = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
      const scp = stripped.codePointAt(0);
      out.push(scp != null && scp >= 0x20 && scp <= 0x7e ? scp : 0x3f);
    }
  }
  return out;
}

/** Bytes del prólogo: init + selección de codepage CP850. */
export const ESC_POS_PROLOGUE = [0x1b, 0x40, 0x1b, 0x74, 0x02];

/** Bytes de cierre: feed para que la última línea pase la cuchilla + corte total. */
export const ESC_POS_CUT = [0x1b, 0x64, 0x04, 0x1d, 0x56, 0x00];
